import { type Placement } from '@atlaskit/dropdown-menu/types';
import ChevronRightIcon from '@atlaskit/icon/core/chevron-right';
import { MenuGroup, Section } from '@atlaskit/menu';
import Popup, { type PopupProps, type TriggerProps } from '@atlaskit/popup';
import Tooltip, { type PositionType } from '@atlaskit/tooltip';
import { useCommentStateContext } from '@databases/comments/hooks/useCommentContext';
import { isEmbed } from '@databases/modes';
import React, {
  type FunctionComponent,
  useCallback,
  useMemo,
  useState,
  useEffect,
} from 'react';

import ButtonItem from './ButtonItem';
import { type MenuData } from './contextual-menu-state';
import { useContextualMenu } from './hooks/useContextualMenu';

const CONTEXTUAL_MENU_MIN_WIDTH = 242;
const CONTEXTUAL_MENU_EMBED_MAX_HEIGHT = 246;

type Content = string | React.ReactNode | MenuItems<string | React.ReactNode>;

export interface MenuItem<C extends Content = Content> {
  key: string;
  content: C;
  name?: string;
  shortcut?: string;
  elemBefore?: React.ReactElement;
  elemAfter?: React.ReactElement;
  tooltipDescription?: string;
  tooltipPosition?: PositionType;
  isActive?: boolean;
  isDisabled?: boolean;
  closeOnClick?: boolean;
  onClick?: (event: React.MouseEvent | React.KeyboardEvent) => void;
  onMouseEnter?: (event: React.MouseEvent | React.KeyboardEvent) => void;
  onMouseLeave?: (event: React.MouseEvent | React.KeyboardEvent) => void;
}

export type MenuItems<C extends Content = Content> = Array<{
  items: MenuItem<C>[];
  hasNoSeparator?: boolean;
  sectionTitle?: string;
}>;

type NestedPopupProps = {
  items: MenuItems;
  onClose: (event?: Event | React.MouseEvent | React.KeyboardEvent) => void;
};

const NestedPopup: FunctionComponent<NestedPopupProps> = ({ items, onClose }) => {
  const [isSubmenuOpen, setIsSubmenuOpen] = useState(false);
  const toggleSubmenu = () => setIsSubmenuOpen((isOpen) => !isOpen);
  const { openThread } = useCommentStateContext();
  const nonEmptyGroups = items.filter((group) => group.items.length > 0);

  useEffect(() => {
    if (openThread.valueId) {
      if (onClose) {
        onClose();
      }
    }
  }, [onClose, openThread.valueId]);

  const handleItemClick = useCallback(
    (item: MenuItem, e: React.MouseEvent | React.KeyboardEvent) => {
      toggleSubmenu();
      item.onClick?.(e);
      if (item.closeOnClick) {
        onClose?.(e);
      }
    },
    [onClose]
  );

  const handleMouseEnter = useCallback(
    (item: MenuItem, e: React.MouseEvent | React.KeyboardEvent) => {
      if (Array.isArray(item.content)) {
        setIsSubmenuOpen(true);
      } else {
        setIsSubmenuOpen(false);
      }
      item.onMouseEnter?.(e);
    },
    []
  );

  const menuMaxHeight = isEmbed() ? CONTEXTUAL_MENU_EMBED_MAX_HEIGHT : undefined;
  const shouldShowSeparator = useCallback(
    (index: number) => {
      const menuGroup = items[index];
      const hasPreviousItems = items[index - 1]?.items?.length > 0;
      const hasNextItems = items[index]?.items?.length > 0;
      return index > 0 && !menuGroup.hasNoSeparator && hasPreviousItems && hasNextItems;
    },
    [items]
  );

  return (
    <MenuGroup
      minWidth={CONTEXTUAL_MENU_MIN_WIDTH}
      maxHeight={menuMaxHeight}
      onClick={(e) => e.stopPropagation()}
      data-testid="database-context-menu"
    >
      {nonEmptyGroups.map((group, index) => (
        <Section
          hasSeparator={shouldShowSeparator(index)}
          title={group.sectionTitle}
          key={index}
        >
          {group.items?.map((item, index) => {
            const content = item.content;
            return (
              <Tooltip
                key={index}
                content={item.tooltipDescription}
                position={item.tooltipPosition}
              >
                {Array.isArray(content) ? (
                  <Popup
                    isOpen={isSubmenuOpen}
                    placement="right-start"
                    onClose={toggleSubmenu}
                    content={() => <NestedPopup items={content} onClose={onClose} />}
                    trigger={(triggerProps) => (
                      <ButtonItem
                        {...triggerProps}
                        role="menuitem"
                        isSelected={isSubmenuOpen}
                        onClick={(e) => handleItemClick(item, e)}
                        onMouseEnter={(e) => handleMouseEnter(item, e)}
                        onMouseLeave={item.onMouseLeave}
                        iconBefore={item.elemBefore}
                        iconAfter={
                          item.elemAfter || (
                            <ChevronRightIcon spacing="spacious" label="" />
                          )
                        }
                        isDisabled={item.isDisabled}
                      >
                        {item.name}
                      </ButtonItem>
                    )}
                  />
                ) : typeof content === 'string' ? (
                  <ButtonItem
                    role="menuitem"
                    onClick={(e) => handleItemClick(item, e)}
                    onMouseEnter={(e) => handleMouseEnter(item, e)}
                    onMouseLeave={item.onMouseLeave}
                    iconBefore={item.elemBefore}
                    iconAfter={item.elemAfter}
                    isDisabled={item.isDisabled}
                    isSelected={item.isActive}
                  >
                    {content}
                  </ButtonItem>
                ) : (
                  <>{content}</>
                )}
              </Tooltip>
            );
          })}
        </Section>
      ))}
    </MenuGroup>
  );
};

export type ContextualMenuProps = Pick<
  PopupProps,
  'shouldRenderToParent' | 'shouldDisableFocusLock'
> & {
  menuData: MenuData;
  items: MenuItems<Content>;
  trigger: (props: TriggerProps) => React.ReactNode;
  onClose?: (event?: Event | React.MouseEvent | React.KeyboardEvent | null) => void;
  offset?: [number, number];
  boundariesElement?: HTMLElement;
  placement?: Placement;
  shouldDisableFocusLock?: boolean;
  panel?: boolean;
  isActionBarEnabled?: boolean;
};

export const ContextualMenu: FunctionComponent<ContextualMenuProps> = ({
  menuData,
  trigger: Trigger,
  items,
  onClose,
  offset,
  boundariesElement = document.body,
  placement = 'right-start',
  shouldRenderToParent,
  shouldDisableFocusLock,
  panel = false,
  isActionBarEnabled = false,
}) => {
  const { closeMenu, isMenuOpen, isPanel } = useContextualMenu(menuData);
  const handleCloseMenu = useCallback(
    (e?: Event | React.MouseEvent | React.KeyboardEvent | null) => {
      closeMenu();
      onClose?.(e);
    },
    [closeMenu, onClose]
  );
  const isPopupOpen = useMemo(
    () => isMenuOpen && items.length > 0 && items.some((item) => item.items.length > 0),
    [isMenuOpen, items]
  );

  const renderPopup = !isActionBarEnabled ? isPopupOpen && isPanel === panel : false;

  return (
    <Popup
      role="menu"
      shouldDisableFocusLock={shouldDisableFocusLock}
      shouldRenderToParent={shouldRenderToParent}
      isOpen={renderPopup}
      onClose={handleCloseMenu}
      content={() => <NestedPopup items={items} onClose={handleCloseMenu} />}
      placement={placement}
      trigger={Trigger}
      offset={offset}
      boundary={boundariesElement}
      id="database-contextual-menu"
      data-testid="database-contextual-menu"
    />
  );
};

export default ContextualMenu;
